import React from 'react';
import { Link } from 'react-router-dom';
import {
    Mail,
    Phone,
    MapPin,
    Facebook,
    Instagram,
    Linkedin,
    ArrowUpRight,
    Globe,
    Leaf
} from 'lucide-react';
import logo from '../assets/logo.png';

export default function Footer() {
    // BRAND COLORS
    const darkGreen = "#0B1A10";
    const goldAccent = "#D4AF37";
    const bgPattern = "https://www.transparenttextures.com/patterns/natural-paper.png";

    const footerNav = [
        { name: 'Home', path: '/home' },
        { name: 'About Us', path: '/Aboutus' },
        { name: 'Treatments', path: '/treatments' },
        { name: 'Memorial Library', path: '/library' },
        { name: 'Annual Projects', path: '/projects/annual' },
        { name: 'Completed Projects', path: '/projects/completed' },
        { name: 'Gallery', path: '/gallery' },
        { name: 'Blog', path: '/blog' },
        { name: 'E-Magazine', path: '/emagazine' }, // Added E-Magazine item
        { name: 'Contact', path: '/contact' },
    ];

    return (
        <footer className="relative pt-32 pb-12 px-8 overflow-hidden text-white" style={{ backgroundColor: darkGreen }}>

            {/* 1. BACKGROUND LAYER */}
            <div className="absolute inset-0 z-0">
                <img
                    src="https://images.unsplash.com/photo-1596715421563-360759089981?q=80&w=2000"
                    className="w-full h-full object-cover opacity-15 mix-blend-screen"
                    alt="Traditional Medicinal Herbs Collection"
                />
                <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: `url(${bgPattern})` }} />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0B1A10] via-transparent to-[#0B1A10]" />
            </div>

            {/* 2. CONTENT LAYER */}
            <div className="relative z-20 max-w-7xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 mb-24">

                    {/* BRAND & INTRO */}
                    <div className="space-y-8">
                        <div className="bg-white p-5 inline-block border-l-4 shadow-2xl" style={{ borderColor: goldAccent }}>
                            <div className="flex items-center gap-3">
                                <img src={logo} alt="Boseth Logo" className="w-10 h-10 object-contain" />
                                <div className="flex flex-col">
                                    <span className="font-serif text-2xl text-[#0B1A10] leading-none uppercase">Boseth</span>
                                    <span style={{ color: goldAccent }} className="text-[8px] font-bold tracking-[0.3em] uppercase">Wellness Foundation</span>
                                </div>
                            </div>
                        </div>
                        <p className="text-base font-serif italic text-white/50 leading-relaxed pr-6">
                            "Roots in heritage, healing for the soul."
                        </p>
                        <p className="text-xs font-light tracking-widest text-white/40 uppercase leading-loose">
                            Sharing the sacred wisdom <br/> of traditional herbal connection.
                        </p>
                    </div>

                    {/* CONTACT INFO */}
                    <div className="space-y-8">
                        <h4 className="uppercase tracking-[0.5em] text-[10px] font-bold flex items-center gap-2" style={{ color: goldAccent }}>
                            <div className="w-8 h-[1px] bg-white/20" /> Reach Out
                        </h4>
                        <div className="space-y-6 text-[13px] font-light text-white/60">
                            <div className="flex items-start gap-4 group">
                                <MapPin size={18} style={{ color: goldAccent }} className="shrink-0" />
                                <span className="group-hover:text-white transition-colors">No : 612, Yowun Mawatha, <br/>Thewaththa, Ragama, Sri Lanka</span>
                            </div>
                            <div className="flex items-center gap-4 group">
                                <Phone size={18} style={{ color: goldAccent }} />
                                <span className="group-hover:text-white transition-colors">+94 71 922 72 27</span>
                            </div>
                            <div className="flex items-center gap-4 group">
                                <Mail size={18} style={{ color: goldAccent }} />
                                <span className="group-hover:text-white transition-colors">BosethWellnessFoundation@outlook.com</span>
                            </div>
                        </div>
                    </div>

                    {/* NAVIGATION */}
                    <div className="space-y-8">
                        <h4 className="uppercase tracking-[0.5em] text-[10px] font-bold flex items-center gap-2" style={{ color: goldAccent }}>
                            <div className="w-8 h-[1px] bg-white/20" /> Navigation
                        </h4>
                        <ul className="grid grid-cols-1 gap-y-4 text-[11px] font-bold uppercase tracking-[0.2em] text-white/30">
                            {footerNav.map((item) => (
                                <li key={item.name}>
                                    <Link to={item.path} className="hover:text-white flex items-center justify-between group transition-all pr-12">
                                        {item.name}
                                        <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-all text-[#D4AF37]" />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* STAY CONNECTED */}
                    <div className="space-y-8">
                        <h4 className="uppercase tracking-[0.5em] text-[10px] font-bold flex items-center gap-2" style={{ color: goldAccent }}>
                            <div className="w-8 h-[1px] bg-white/20" /> Connection
                        </h4>
                        <p className="text-[12px] text-white/40 font-light leading-relaxed">
                            Be part of our mission to preserve ancestral healing methods.
                        </p>
                        <div className="relative border-b border-white/10 group focus-within:border-[#D4AF37] transition-all">
                            <input
                                type="email"
                                placeholder="YOUR EMAIL ADDRESS"
                                className="w-full bg-transparent py-4 text-[10px] tracking-[0.2em] outline-none placeholder:text-white/20 text-white"
                            />
                            <button className="absolute right-0 bottom-4 text-[10px] font-bold tracking-[0.3em] hover:text-[#D4AF37] transition-colors">
                                JOIN
                            </button>
                        </div>
                        <div className="flex gap-6 pt-4">
                            {[Facebook, Instagram, Linkedin].map((Icon, idx) => (
                                <Icon
                                    key={idx}
                                    size={18}
                                    className="hover:text-[#D4AF37] transition-all cursor-pointer opacity-30 hover:opacity-100"
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {/* BOTTOM BAR */}
                <div className="pt-12 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-8">
                    <div className="text-[9px] uppercase tracking-[0.4em] text-white/20 font-medium flex items-center gap-3">
                        <Leaf size={12} style={{ color: goldAccent }} />
                        © 2026 Boseth Wellness Foundation.
                    </div>
                    <div className="flex gap-10 text-[9px] uppercase tracking-[0.3em] font-bold text-white/20">
                        <Link to="/privacy" className="hover:text-white transition">Privacy Policy</Link>
                        <Link to="/terms" className="hover:text-white transition">Terms of Service</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}